import { createContext, useState, useEffect } from "react";
import Cookies from "universal-cookie";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [isSuperAdminAuthenticated, setIsSuperAdminAuthenticated] =
    useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    const cookies = new Cookies();

    // validate customer coolie with backend
    const checkCustomerCookie = async () => {
      try {
        const cookieToken = cookies.get("customer"); // Get token from cookie
        if (!cookieToken) {
          return false;
        }

        const res = await fetchWithTimeout(
          `${process.env.REACT_APP_URL}/api/customer/validateCookie`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ token: cookieToken }), // token in body
          },
          30000
        );

        const data = await res.json();
        return res.ok && data.status === true;
      } catch (error) {
        console.error("Error checking customer authentication: ", error);
        return false;
      }
    };

    // validate admin/agent cookie with backend
    const checkAdminCookie = async () => {
      try {
        const cookieToken = cookies.get("admin"); // Get token from cookie
        if (!cookieToken) {
          return false;
        }

        // send token in request body
        const res = await fetchWithTimeout(
          `${process.env.REACT_APP_URL}/api/agent/validateCookie`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: cookieToken }),
          },
          30000
        );
        const data = await res.json();
        return res.ok && data.status === true;
      } catch (error) {
        console.error("Error checking admin authentication: ", error);
        return false;
      }
    };

    // Validate superadmin cookie with backend
    const checkSuperAdminCookie = async () => {
      try {
        const cookieToken = cookies.get("superadmin"); // Get token from cookie

        if (!cookieToken) {
          return false;
        }

        // Send token in request BODY
        const res = await fetchWithTimeout(
          `${process.env.REACT_APP_URL}/api/admin/validateCookie`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: cookieToken }),
          },
          30000
        );

        const data = await res.json();
        return res.ok && data.status === true;
      } catch (error) {
        console.error("Error checking superadmin authentication:", error);
        return false;
      }
    };


    const checkAllAuth = async () => {
      setAuthLoading(true);
      const currentPath = window.location.pathname;
      // Check authentication based on current path
      if (currentPath.startsWith("/admin")) {
        const isAuthenticated = await checkAdminCookie();
        setIsAdminAuthenticated(isAuthenticated);
      } else if (currentPath.startsWith("/superadmin")) {
        const isAuthenticated = await checkSuperAdminCookie();
        setIsSuperAdminAuthenticated(isAuthenticated);
      } else if (currentPath.startsWith("/crm")) {
        const isAuthenticated = await checkCustomerCookie();
        setIsUserAuthenticated(isAuthenticated);
      } else {
        // On public pages, check all three (for LoginWrapper redirects)
        const [userAuth, adminAuth, superAdminAuth] = await Promise.all([
          checkCustomerCookie(),
          checkAdminCookie(),
          checkSuperAdminCookie(),
        ]);
        setIsUserAuthenticated(userAuth);
        setIsAdminAuthenticated(adminAuth);
        setIsSuperAdminAuthenticated(superAdminAuth);
      }

      setAuthLoading(false);
    };

    checkAllAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isUserAuthenticated,
        isAdminAuthenticated,
        isSuperAdminAuthenticated,
        setIsUserAuthenticated,
        setIsAdminAuthenticated,
        setIsSuperAdminAuthenticated,
        authLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
